/* eslint-disable indent */
import { useState, useCallback, useContext, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';
import { IconCircleDashedCheck, IconHandStop } from '@tabler/icons-react';
import Card from '@/components/Card';
import Canvas from '@/components/Custom/Canvas';
import { usePostFileSFTP } from '@/hooks/sftp.hooks';
import Loader from '@/components/Loading/Loader';
import ListElementCar from '@/components/Custom/ListElementCar';
import { IconPlayerTrackNext } from '@tabler/icons-react';
import {
  useUpdateCarProcessedToModerate,
  useUpdateFirestoreImage,
  useUpdateProcessing,
  useUploadProcessingImage,
} from '@/hooks/ai.hooks';
import useReportData from '@/hooks/useReportData';
import { ElementType } from '@/entities/ia';
import { CORRECTION_TYPE, R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';
import { getColorByValue, getFileToUpload, initReport } from '@/utils/ia.utils';
import { Processing } from '@/api/entities/processing';
import { FileData } from '@/api/entities/engineCorrection';
import { createFileFromUrl } from '@/utils/file.utils';
import { MODE_DRAW } from '@/utils/constant.utils';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import { useGetModerationEquipments } from '@/hooks/equipment.hooks';
import { useGetModerationDamages } from '@/hooks/damage.hooks';
import { useTranslation } from 'react-i18next';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { TabList } from '@/components/Tab/TabList';
import { TabItem } from '@/components/Tab/TabItem';
import { twMerge } from 'tailwind-merge';
import { RotationIcon } from '@/components/SvgIcons/RotationIcon';
import { ModerationReportCanvas } from '@/components/Custom/ModerationReportCanvas';
import { BackButton } from '@/components/Custom/Button/BackButton';
import { getEquipmentByClassId } from '@/api/equipment/equipment';
import { getDamageByClassId } from '@/api/damage/damage';

const FIREBASE_BASE_URL = 'https://firebasestorage.googleapis.com';

interface EngineCorrectionModerationProps {
  processingId?: string;
  backToLink?: string;
  showOnlyEquipments?: boolean;
  showOnlyDamages?: boolean;
  showOnlyDetections?: boolean;
  showOnlyModerations?: boolean;
  equipmentClassId?: number;
}

export const EngineCorrectionModeration = ({
  processingId,
  backToLink,
  showOnlyEquipments = false,
  showOnlyDamages = false,
  showOnlyDetections = false,
  showOnlyModerations = false,
  equipmentClassId,
}: EngineCorrectionModerationProps) => {
  const { t } = useTranslation('engineCorrection');

  const {
    setReportEngineData,
    listElement,
    setListElement,
    listDamage,
    setListDamage,
    dataAnnotation,
    setDataAnnotation,
    dataAnnotationDamage,
    setDataAnnotationDamage,
    currentElement,
    setIsdDeleted,
    setSendLogs,
    setNexImage,
  } = useContext(EngineCorrectionContext);

  const navigate = useNavigate();
  const { id } = useParams();

  const currentId = processingId || id;

  const engineContinueRef = useRef<boolean>();
  const rotationCountRef = useRef<number>(0);
  const newImageUrlRef = useRef<string | null>(null);
  const transformedAnnotationRef = useRef<TResultImageCoordinatesSchema[]>([]);
  const transformedDamageAnnotationRef = useRef<
    TResultImageCoordinatesSchema[]
  >([]);
  const transformedListElementRef = useRef<ElementType[]>([]);
  const transformedListDamageRef = useRef<ElementType[]>([]);

  const { dataCar, isPending, carProcessedNext, generateReportData } =
    useReportData({
      id: currentId,
      showOnlyDetections,
      showOnlyModerations,
      equipmentClassId,
    });

  const [showElement, setShowElement] = useState<boolean>(true);
  const [showDamage, setShowDamage] = useState<boolean>(false);
  const [correctionNumber, setCorrectionNumber] = useState<{
    correctionElNb?: number;
    correctionDmgNb?: number;
  }>({ correctionElNb: 0, correctionDmgNb: 0 });
  const [rotationAngle, setRotationAngle] = useState<number>(0);
  const [temporaryImageUrl, setTemporaryImageUrl] = useState<string | null>(
    null
  );
  const [isLoadingRotatedUrl, setIsLoadingRotatedUrl] =
    useState<boolean>(false);

  const [isLoadingDataByClassId, setIsLoadingDataByClassId] =
    useState<boolean>(false);

  const { equipmentsSelectOptions } = useGetModerationEquipments();
  const { allDamagesSelectOptions } = useGetModerationDamages();

  const { mutate: updateFirestoreImage, isPending: isUpdatingFirestoreImage } =
    useUpdateFirestoreImage();

  const { mutate: updateProcessing, isPending: isPendingUpdateProcessing } =
    useUpdateProcessing({
      handleSuccess: () => {
        updateFirestoreImage({
          previousImageUrl:
            rotationCountRef.current === 1
              ? dataCar?.imageUrl
              : newImageUrlRef.current,
          imageUrl: newImageUrlRef.current,
        });
      },
    });

  const { mutate: uploadRotatedImage, isPending: isUploadingRotatedImage } =
    useUploadProcessingImage({
      handleSuccess: (data: {
        data: { files: Array<{ filename: string; path: string }> };
      }) => {
        // Get the first uploaded file (since we're only uploading one rotated image)
        const uploadedFile = data.data.files[0];
        newImageUrlRef.current = `${R2_PUBLIC_DOMAIN}/${uploadedFile.path}`;

        // Preload the new image to ensure it's ready before switching
        const newImage = new Image();
        newImage.onload = () => {
          // Only switch to new URL and clean up temporary URL after new image has loaded
          if (temporaryImageUrl) {
            URL.revokeObjectURL(temporaryImageUrl);
          }
          setTemporaryImageUrl(newImageUrlRef.current);
          setIsLoadingRotatedUrl(false);
        };
        newImage.onerror = () => {
          // If new image fails to load, keep the temporary URL and stop loading
          console.error('Failed to load new rotated image');
          setIsLoadingRotatedUrl(false);
        };
        newImage.src = newImageUrlRef.current;

        const fileName = uploadedFile.filename?.replace('.jpg', '');

        updateProcessing({
          id: dataCar?.id,
          publicUrl: newImageUrlRef.current,
          fileName: newImageUrlRef.current,
          fileNameElementsDetected: fileName,
          fileNameDamagesDetected: fileName,
        });
      },
    });

  const { mutate: updateProcess, isPending: isUpdateProcess } =
    useUpdateCarProcessedToModerate(() => {
      const dataNext = carProcessedNext?.data as Processing;
      setListElement([]);
      setListDamage([]);
      setDataAnnotation([]);
      setNexImage(dataNext?.id);
      setDataAnnotationDamage([]);
      if (engineContinueRef.current == true) {
        navigate(`/engine-correction/${dataNext?.id}`);
      } else if (engineContinueRef.current == false) {
        if (processingId) {
          navigate(backToLink);
        } else {
          navigate('/moteur-de-correction-liste');
        }
      }
    });

  const { mutate, isPending: isUpload } = usePostFileSFTP();

  const isLoadingModeration = isPending || isUpload || isUpdateProcess;

  const isLoadingRotation =
    isUploadingRotatedImage ||
    isPendingUpdateProcessing ||
    isUpdatingFirestoreImage ||
    isLoadingRotatedUrl;

  const isLoadingAll =
    isLoadingModeration || isLoadingRotation || isLoadingDataByClassId;

  const updateCorrectionNumber = () => {
    setCorrectionNumber((prev) =>
      showElement
        ? {
            ...prev,
            correctionElNb: (prev.correctionElNb || 0) + 1,
          }
        : {
            ...prev,
            correctionDmgNb: (prev.correctionDmgNb || 0) + 1,
          }
    );
  };

  const confirmAddElement = () => {
    updateCorrectionNumber();
    setSendLogs(true);
  };

  const confirmDeleteElement = useCallback(() => {
    if (showElement) {
      const actualElement = [...listElement];
      const actualAnnotation = [...dataAnnotation];
      setListElement(
        actualElement.filter((_el, index) => index !== currentElement['index'])
      );
      setDataAnnotation(
        actualAnnotation.filter(
          (_annotation, index) => index !== currentElement['index']
        )
      );
      setReportEngineData((prev) => ({
        ...prev,
        remove: [
          ...prev.remove,
          {
            ...initReport(currentId),
            isCorrectElements: false,
            elmtCorrectionTypes: CORRECTION_TYPE.SUPPRESSION,
            elmtBeforeCorrection: currentElement['elNumber']?.toString(),
          },
        ],
      }));
    }
    if (showDamage) {
      const actualDamage = [...listDamage];
      const actualAnnotation = [...dataAnnotationDamage];
      setListDamage(
        actualDamage.filter((_el, index) => index !== currentElement['index'])
      );
      setDataAnnotationDamage(
        actualAnnotation.filter(
          (_annotation, index) => index !== currentElement['index']
        )
      );

      setReportEngineData((prev) => ({
        ...prev,
        remove: [
          ...prev.remove,
          {
            ...initReport(currentId),
            isCorrectDamages: false,
            dmgCorrectionTypes: CORRECTION_TYPE.SUPPRESSION,
            dmgBeforeCorrection: currentElement['elNumber'].toString(),
          },
        ],
      }));
    }

    // Clear transformation refs to prevent deleted elements from being restored during rotation
    transformedAnnotationRef.current = [];
    transformedDamageAnnotationRef.current = [];
    transformedListElementRef.current = [];
    transformedListDamageRef.current = [];

    updateCorrectionNumber();
    setIsdDeleted(true);
    setSendLogs(true);
  }, [currentElement]);

  const onEditElement = (newValue: string) => {
    if (showElement) {
      const actualElement = [...listElement];
      const actualAnnotation = [...dataAnnotation];
      const elementUpdated = actualElement.map((el, index) =>
        index === currentElement['index']
          ? ({
              elNumber: newValue,
              color: getColorByValue(
                newValue,
                showElement,
                equipmentsSelectOptions,
                allDamagesSelectOptions
              ),
            } as ElementType)
          : { ...el }
      );
      const annotationUpdate = actualAnnotation.map((el, index) =>
        index === currentElement['index']
          ? ({ ...el, elNumber: newValue } as TResultImageCoordinatesSchema)
          : { ...el }
      );
      setListElement([...elementUpdated]);
      setDataAnnotation([...annotationUpdate]);
    }
    if (showDamage) {
      const actualDamage = [...listDamage];
      const actualDmgAnnotation = [...dataAnnotationDamage];
      const dmgUpdated = actualDamage.map((el, index) =>
        index === currentElement['index']
          ? ({
              elNumber: newValue,
              color: getColorByValue(
                newValue,
                showElement,
                equipmentsSelectOptions,
                allDamagesSelectOptions
              ),
            } as ElementType)
          : { ...el }
      );
      const annotationUpdate = actualDmgAnnotation.map((el, index) =>
        index === currentElement['index']
          ? ({ ...el, elNumber: newValue } as TResultImageCoordinatesSchema)
          : { ...el }
      );
      setListDamage([...dmgUpdated]);
      setDataAnnotationDamage([...annotationUpdate]);
    }

    // Clear transformation refs to prevent stale data from being restored during rotation
    transformedAnnotationRef.current = [];
    transformedDamageAnnotationRef.current = [];
    transformedListElementRef.current = [];
    transformedListDamageRef.current = [];

    updateCorrectionNumber();
    setSendLogs(true);
  };

  const getEquipmentsByClassId = async () => {
    try {
      if (listElement?.length > 0) {
        setIsLoadingDataByClassId(true);
        const equipments = await Promise.all(
          listElement.map(async (element) => {
            const response = await getEquipmentByClassId(
              Number(element.elNumber)
            );
            return response.data?.name;
          })
        );
        return equipments;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingDataByClassId(false);
    }

    return [];
  };

  const getDamagesByClassId = async () => {
    try {
      if (listDamage?.length > 0) {
        setIsLoadingDataByClassId(true);
        const damages = await Promise.all(
          listDamage.map(async (damage) => {
            const response = await getDamageByClassId(Number(damage.elNumber));
            return response.data?.name;
          })
        );
        return damages;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingDataByClassId(false);
    }

    return [];
  };

  const getEquipmentModeratedAnnotation = () => {
    return dataAnnotation?.length > 0
      ? dataAnnotation?.map((annotation) => {
          return {
            ...annotation,
            elNumber: Number(annotation.elNumber),
          };
        })
      : [];
  };

  const getDamagesModeratedAnnotation = () => {
    return dataAnnotationDamage?.length > 0
      ? dataAnnotationDamage?.map((annotation) => {
          return {
            ...annotation,
            elNumber: Number(annotation.elNumber),
          };
        })
      : [];
  };

  const validModeration = useCallback(async () => {
    if (!dataAnnotation && !dataAnnotationDamage) return;
    const fileName = dataCar.fileName;
    const fileData: FileData = {
      elementAnnotation: null,
      dmgAnnotation: null,
      image: null,
    };

    const imageToUpload = await createFileFromUrl(dataCar.imageUrl, fileName);

    const moderatedClassesElements = await getEquipmentsByClassId();
    const moderatedClassesDamages = await getDamagesByClassId();

    const moderationsElements = getEquipmentModeratedAnnotation();
    const moderationsDamages = getDamagesModeratedAnnotation();

    updateProcess({
      id: currentId,
      correctionElNb: correctionNumber.correctionElNb,
      correctionDmgNb: correctionNumber.correctionDmgNb,
      moderatedClassesElements,
      moderationsElements,
      moderatedClassesDamages,
      moderationsDamages,
    });

    if (dataAnnotation.length > 0) {
      const fileElementUpload = getFileToUpload(dataAnnotation, dataCar, false);
      fileData.elementAnnotation = fileElementUpload;
      fileData.image = imageToUpload;
    }

    if (dataAnnotationDamage.length > 0) {
      const fileDamageUpload = getFileToUpload(
        dataAnnotationDamage,
        dataCar,
        true
      );
      fileData.dmgAnnotation = fileDamageUpload;
      fileData.image = imageToUpload;
    }

    if (dataAnnotation.length > 0 || dataAnnotationDamage.length > 0)
      mutate({ fileData });

    generateReportData();
  }, [isUpload, dataCar, dataAnnotation, dataAnnotationDamage]);

  const validStop = () => {
    engineContinueRef.current = false;
    validModeration();
  };

  const noValidStop = () => {
    const dataNext = carProcessedNext?.data as Processing;
    setNexImage(dataNext?.id);
    setListElement([]);
    setListDamage([]);
    setDataAnnotation([]);
    setDataAnnotationDamage([]);
    navigate('/moteur-de-correction-liste');
  };

  // Clear transformation refs whenever state changes to prevent stale data restoration
  useEffect(() => {
    // Only clear if not currently in the middle of a rotation
    if (!isLoadingRotation) {
      transformedAnnotationRef.current = [];
      transformedDamageAnnotationRef.current = [];
      transformedListElementRef.current = [];
      transformedListDamageRef.current = [];
    }
  }, [
    listElement,
    listDamage,
    dataAnnotation,
    dataAnnotationDamage,
    isLoadingRotation,
  ]);

  // Restore transformed coordinates after image rotation
  useEffect(() => {
    if (
      dataCar &&
      dataCar.imageUrl &&
      (transformedAnnotationRef.current.length > 0 ||
        transformedDamageAnnotationRef.current.length > 0 ||
        transformedListElementRef.current.length > 0 ||
        transformedListDamageRef.current.length > 0)
    ) {
      if (transformedAnnotationRef.current.length > 0) {
        setDataAnnotation([...transformedAnnotationRef.current]);
      }
      if (transformedDamageAnnotationRef.current.length > 0) {
        setDataAnnotationDamage([...transformedDamageAnnotationRef.current]);
      }
      if (transformedListElementRef.current.length > 0) {
        setListElement([...transformedListElementRef.current]);
      }
      if (transformedListDamageRef.current.length > 0) {
        setListDamage([...transformedListDamageRef.current]);
      }

      // Clear the refs after restoring
      transformedAnnotationRef.current = [];
      transformedDamageAnnotationRef.current = [];
      transformedListElementRef.current = [];
      transformedListDamageRef.current = [];
    }
  }, [dataCar?.imageUrl]);

  // Cleanup temporary URL on unmount or when dataCar changes
  useEffect(() => {
    return () => {
      if (temporaryImageUrl) {
        URL.revokeObjectURL(temporaryImageUrl);
      }
    };
  }, [temporaryImageUrl]);

  // Clear temporary URL when dataCar changes
  // useEffect(() => {
  //   if (temporaryImageUrl) {
  //     URL.revokeObjectURL(temporaryImageUrl);
  //     setTemporaryImageUrl(null);
  //   }
  // }, [dataCar?.id]);

  useEffect(() => {
    if (isLoadingRotatedUrl) {
      setIsLoadingRotatedUrl(false);
    }
  }, [dataCar]);

  const skippedModeration = useCallback(async () => {
    engineContinueRef.current = true;
    updateProcess({
      id: currentId,
      correctionElNb: correctionNumber.correctionElNb,
      correctionDmgNb: correctionNumber.correctionDmgNb,
    });
  }, [
    updateProcess,
    currentId,
    correctionNumber.correctionElNb,
    correctionNumber.correctionDmgNb,
  ]);

  const validateAndContinue = useCallback(async () => {
    engineContinueRef.current = true;
    validModeration();
  }, [validModeration]);

  const rotateImage = async (
    imageUrl: string,
    angle: number
  ): Promise<Blob> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve(new Blob());
          return;
        }

        // Calculate canvas dimensions based on rotation
        const radians = (angle * Math.PI) / 180;
        const sin = Math.abs(Math.sin(radians));
        const cos = Math.abs(Math.cos(radians));

        const newWidth = img.width * cos + img.height * sin;
        const newHeight = img.width * sin + img.height * cos;

        canvas.width = newWidth;
        canvas.height = newHeight;

        // Move to center, rotate, and draw
        ctx.translate(newWidth / 2, newHeight / 2);
        ctx.rotate(radians);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        canvas.toBlob(
          (blob) => {
            resolve(blob || new Blob());
          },
          'image/jpeg',
          0.9
        );
      };
      img.src = imageUrl;
    });
  };

  const transformCoordinatesForRotation = (
    coordinates: TResultImageCoordinatesSchema[],
    rotationAngle: number
  ): TResultImageCoordinatesSchema[] => {
    return coordinates.map((coord) => {
      const transformedCoord = { ...coord };

      // Transform coordinates based on rotation angle
      // Note: coordinates are normalized (0-1) relative to image dimensions
      switch (rotationAngle) {
        case 90:
          // 90° clockwise rotation
          if (
            coord.xCenter !== undefined &&
            coord.yCenter !== undefined &&
            coord.xCenter !== null &&
            coord.yCenter !== null
          ) {
            transformedCoord.xCenter = coord.yCenter;
            transformedCoord.yCenter = 1 - coord.xCenter;
          }
          if (
            coord.width !== undefined &&
            coord.height !== undefined &&
            coord.width !== null &&
            coord.height !== null
          ) {
            // Swap width and height for 90° rotation
            transformedCoord.width = coord.height;
            transformedCoord.height = coord.width;
          }
          // Transform polygon points if they exist
          if (coord.point && coord.point.length > 0) {
            transformedCoord.point = coord.point.map((point) => ({
              x: point.y !== undefined && point.y !== null ? point.y : 0,
              y: point.x !== undefined && point.x !== null ? 1 - point.x : 0,
            }));
          }
          break;
        case 180:
          // 180° rotation
          if (
            coord.xCenter !== undefined &&
            coord.yCenter !== undefined &&
            coord.xCenter !== null &&
            coord.yCenter !== null
          ) {
            transformedCoord.xCenter = 1 - coord.xCenter;
            transformedCoord.yCenter = 1 - coord.yCenter;
          }
          // Width and height remain the same for 180° rotation
          // Transform polygon points if they exist
          if (coord.point && coord.point.length > 0) {
            transformedCoord.point = coord.point.map((point) => ({
              x: point.x !== undefined && point.x !== null ? 1 - point.x : 0,
              y: point.y !== undefined && point.y !== null ? 1 - point.y : 0,
            }));
          }
          break;
        // case 270:
        //   // 270° clockwise rotation (or 90° counter-clockwise)
        //   if (
        //     coord.xCenter !== undefined &&
        //     coord.yCenter !== undefined &&
        //     coord.xCenter !== null &&
        //     coord.yCenter !== null
        //   ) {
        //     transformedCoord.xCenter = coord.yCenter;
        //     transformedCoord.yCenter = 1 - coord.xCenter;
        //   }
        //   if (
        //     coord.width !== undefined &&
        //     coord.height !== undefined &&
        //     coord.width !== null &&
        //     coord.height !== null
        //   ) {
        //     // Swap width and height for 270° rotation
        //     transformedCoord.width = coord.height;
        //     transformedCoord.height = coord.width;
        //   }
        //   // Transform polygon points if they exist
        //   if (coord.point && coord.point.length > 0) {
        //     transformedCoord.point = coord.point.map((point) => ({
        //       x: point.y !== undefined && point.y !== null ? point.y : 0,
        //       y:
        //         point.x !== undefined && point.x !== null
        //           ? 1 - point.x
        //           : point.y !== undefined && point.y !== null
        //           ? point.y
        //           : 0,
        //     }));
        //   }
        //   break;
        default:
          // No transformation needed for 0° or invalid angles
          break;
      }

      return transformedCoord;
    });
  };

  const handleRotateImage = async () => {
    if (!dataCar?.imageUrl) return;

    // Increment rotation count
    rotationCountRef.current++;

    let newRotationAngle = (rotationAngle + 90) % 180;

    // Skip 0° rotation as it doesn't actually change the image
    if (newRotationAngle === 0) {
      newRotationAngle = 90;
    }

    setRotationAngle(newRotationAngle);

    try {
      // Transform coordinates for both equipment and damage annotations
      const transformedDataAnnotation = transformCoordinatesForRotation(
        dataAnnotation,
        newRotationAngle
      );

      const transformedDataAnnotationDamage = transformCoordinatesForRotation(
        dataAnnotationDamage,
        newRotationAngle
      );

      // Store transformed coordinates and lists in refs to be restored when new image is ready
      transformedAnnotationRef.current = transformedDataAnnotation;
      transformedDamageAnnotationRef.current = transformedDataAnnotationDamage;
      transformedListElementRef.current = [...listElement];
      transformedListDamageRef.current = [...listDamage];

      // Note: Don't update state here - let the useEffect handle it when the new image URL is set
      // This prevents duplicate annotations from accumulating

      // Determine the current image URL (could be a rotated image from previous rotation)
      const currentImageUrl = newImageUrlRef.current || dataCar.imageUrl;

      const rotatedBlob = await rotateImage(currentImageUrl, newRotationAngle);

      // Create and set temporary URL to show the blob immediately
      const tempUrl = URL.createObjectURL(rotatedBlob);
      setTemporaryImageUrl(tempUrl);
      setIsLoadingRotatedUrl(true);

      const imageName = currentImageUrl.includes(FIREBASE_BASE_URL)
        ? `${crypto.randomUUID()}.jpg`
        : currentImageUrl?.split('/').pop();

      newImageUrlRef.current = `${R2_PUBLIC_DOMAIN}/moderations/images/${imageName}`;

      // Create a File object from the blob
      const rotatedFile = new File([rotatedBlob], imageName, {
        type: 'image/jpeg',
      });

      uploadRotatedImage([rotatedFile]);
    } catch (error) {
      console.error('Error rotating image:', error);
    }
  };

  return (
    <>
      <Card
        title={
          <div className="w-full flex items-center gap-5">
            <div className="flex flex-1">
              <TitleSection
                text={
                  <div className="flex items-center gap-2">
                    {processingId && (
                      <>
                        <BackButton
                          buttonClassName="w-7 min-w-7 h-7 min-h-7 bg-purple-A713F6"
                          disabled={isLoadingAll}
                          onClick={() => {
                            navigate(backToLink);
                          }}
                        />
                        <div className="text-md font-thin">
                          {t('common:back')}
                        </div>
                        <div className="ml-0.5 mt-[1px] text-2sm text-slate-900 font-normal">
                          |
                        </div>
                      </>
                    )}

                    {t('moderateThisElement')}
                  </div>
                }
                defaultIconClassName={processingId ? '' : 'ki-toggle-on'}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <div
                className={twMerge(
                  'flex justify-center items-center bg-primary rounded-full p-2 shadow-lg hover:cursor-pointer',
                  isLoadingAll && 'pointer-events-none opacity-80'
                )}
                onClick={handleRotateImage}
              >
                <RotationIcon className="w-4 min-w-4 h-4 min-h-4 [&>path]:fill-white scale-x-[-1]" />
              </div>
              <div className="text-2sm font-normal text-primary hover:cursor-default">
                {t('changeImageRotation')}
              </div>
            </div>
          </div>
        }
        bodyClassName={twMerge(
          'pt-8 pb-10',
          isLoadingAll && 'pointer-events-none'
        )}
      >
        <div>
          <div className="flex gap-3 mb-5">
            {processingId ? (
              <button
                className={twMerge(
                  'btn btn-success pt-0.5',
                  isLoadingAll && 'disabled'
                )}
                onClick={validStop}
              >
                <IconCircleDashedCheck stroke={2} />
                {isLoadingAll
                  ? t('common:inProgress')
                  : processingId
                  ? t('common:validate')
                  : t('validateAndStop')}
              </button>
            ) : (
              <>
                <button
                  className={twMerge(
                    'btn btn-outline btn-success pt-0.5',
                    isLoadingAll && 'disabled'
                  )}
                  onClick={validateAndContinue}
                >
                  <IconCircleDashedCheck stroke={2} />
                  {isLoadingAll
                    ? t('common:inProgress')
                    : t('validateAndContinueModeration')}
                </button>
                <button
                  className={twMerge(
                    'btn btn-outline btn-success pt-0.5',
                    isLoadingAll && 'disabled'
                  )}
                  onClick={validStop}
                >
                  <IconCircleDashedCheck stroke={2} />
                  {isLoadingAll ? t('common:inProgress') : t('validateAndStop')}
                </button>

                <button
                  className={twMerge(
                    'btn btn-outline btn-danger pt-0.5',
                    isLoadingAll && 'disabled'
                  )}
                  onClick={noValidStop}
                >
                  <IconHandStop stroke={2} />
                  {isLoadingAll
                    ? t('common:inProgress')
                    : t('stopWithoutValidation')}
                </button>

                <button
                  className={twMerge(
                    'btn btn-outline btn-primary pt-0.5',
                    isLoadingAll && 'disabled'
                  )}
                  onClick={skippedModeration}
                >
                  <IconPlayerTrackNext stroke={2} />
                  {isLoadingAll ? t('common:inProgress') : t('common:skip')}
                </button>
              </>
            )}
          </div>
          <div className="flex gap-x-[21px]">
            <div
              className={twMerge(
                'min-w-[868.1px]',
                isLoadingModeration && 'bg-blue-131025'
              )}
            >
              {isLoadingRotation ? (
                <ModerationReportCanvas
                  imgSrc={temporaryImageUrl}
                  mode={MODE_DRAW.POLYGONE}
                />
              ) : (
                <Canvas
                  t={t}
                  imageSrc={temporaryImageUrl || dataCar?.imageUrl}
                  listAnnotation={
                    showElement ? dataAnnotation : dataAnnotationDamage
                  }
                  showElement={showElement}
                  equipmentClassId={equipmentClassId}
                  confirmAddElement={confirmAddElement}
                  mode={showElement ? MODE_DRAW.RECTANGLE : MODE_DRAW.POLYGONE}
                />
              )}
            </div>
            <div
              className={twMerge(
                'min-w-[280.9px]',
                isLoadingAll && 'opacity-80'
              )}
            >
              <div className="font-semibold text-center pb-3 mb-3 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[1px] after:border-b after:border-dashed after:border-b-slate-200">
                {showOnlyDetections
                  ? t('detectionList')
                  : showOnlyModerations
                  ? t('moderationList')
                  : t('detectionOrModerationList')}
              </div>
              {!(showOnlyEquipments || showOnlyDamages) && (
                <TabList className="gap-3 pb-3">
                  <TabItem
                    className={twMerge('', !showElement && 'border-black/50')}
                    text={t('common:equipments')}
                    leftIcon={<i className="ki-outline ki-size" />}
                    active={showElement}
                    onChange={() => {
                      setShowElement(true);
                      setShowDamage(false);
                    }}
                  />
                  <TabItem
                    className={twMerge('', !showDamage && 'border-black/50')}
                    text={t('common:damages')}
                    leftIcon={<i className="ki-outline ki-bucket" />}
                    active={showDamage}
                    onChange={() => {
                      setShowElement(false);
                      setShowDamage(true);
                    }}
                  />
                </TabList>
              )}
              <div id="tab_element-list">
                <ListElementCar
                  t={t}
                  isLoading={isLoadingModeration}
                  listElement={showElement ? listElement : listDamage}
                  showElement={showElement}
                  showOnlyEquipments={showOnlyEquipments}
                  showOnlyDamages={showOnlyDamages}
                  showOnlyDetections={
                    showOnlyDetections || showOnlyModerations === false
                  }
                  equipmentClassId={equipmentClassId}
                  onConfirmModal={confirmDeleteElement}
                  onEdit={onEditElement}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
      {isLoadingAll && <Loader />}
    </>
  );
};
